import { FC, ReactNode, useEffect } from 'react';
import { PhoneOutgoing, MessageSquare, Mail, CheckCircle2 } from 'lucide-react';
import { StatsModal } from './StatsModal';
import { TelLink, EmailLink } from '../../common/ContactLinks';
import { ExpandableTagsCell } from '../../common/ExpandableTagsCell';
import { ExpandableTextCell } from '../../common/ExpandableTextCell';
import { formatDate } from '../../../utils/dateTime';
import { presentText } from '../../../utils/formatters';
import { getFollowUpTypeLabel } from '../../../api/nextFollowUp';
import {
  useFlywheelContactsGQL,
  useFlywheelFollowUpsGQL,
  type GQLFlywheelContact,
  type GQLFollowUpDecision,
} from '../../../hooks/graphql/contacts/useFlywheelFollowUpsGQL';
import type { FlywheelDrilldownFilter } from '../../dashboard/widgetDefinitions';

/**
 * Map a `decisionType` (schedule_voice / schedule_sms / schedule_email
 * / …) to the same icon + colour pair the rest of the app uses for
 * channel chips — call = outbound green, sms = inbound blue, email =
 * ealert violet. Reuses the channel CSS classes from contacts.css so
 * the badge feels native to the dashboard.
 */
function decisionTypeBadge(decisionType: string | null | undefined): {
  icon: ReactNode;
  bgClass: string;
  colorClass: string;
} {
  const t = (decisionType || '').toLowerCase();
  if (t.includes('voice') || t.includes('call')) {
    return {
      icon: <PhoneOutgoing className="w-3 h-3" />,
      bgClass: 'call-icon-outbound',
      colorClass: 'call-color-outbound',
    };
  }
  if (t.includes('sms') || t.includes('text')) {
    return {
      icon: <MessageSquare className="w-3 h-3" />,
      bgClass: 'call-icon-inbound',
      colorClass: 'call-color-inbound',
    };
  }
  if (t.includes('email')) {
    return {
      icon: <Mail className="w-3 h-3" />,
      bgClass: 'ealert-icon-bg',
      colorClass: 'ealert-color',
    };
  }
  return {
    icon: <CheckCircle2 className="w-3 h-3" />,
    bgClass: 'bg-amber-100 dark:bg-amber-500/20',
    colorClass: 'text-amber-700 dark:text-amber-300',
  };
}

interface FlywheelFollowUpsModalProps {
  filter: Extract<FlywheelDrilldownFilter, { kind: 'followups' }>;
  isOpen: boolean;
  onClose: () => void;
  timezone?: string;
  onOpenContact: (contactId: string) => void;
}

interface FlywheelContactsModalProps {
  filter: Extract<FlywheelDrilldownFilter, { kind: 'contacts' }>;
  isOpen: boolean;
  onClose: () => void;
  onOpenContact: (contactId: string) => void;
}

function contactName(decision: GQLFollowUpDecision): string {
  const name = [decision.contactFirstName, decision.contactLastName]
    .map(presentText)
    .filter(Boolean)
    .join(' ');
  return (
    name ||
    presentText(decision.contactEmail) ||
    presentText(decision.contactPhone) ||
    presentText(decision.contactId) ||
    'Unknown contact'
  );
}

function flowNames(contact: GQLFlywheelContact): string[] {
  const names = new Set<string>();
  for (const flow of [...(contact.allFlowExecutions ?? []), ...(contact.flowExecutions ?? [])]) {
    const name = presentText(flow.flowName);
    if (name) names.add(name);
  }
  return Array.from(names);
}

export const FlywheelFollowUpsModal: FC<FlywheelFollowUpsModalProps> = ({
  filter,
  isOpen,
  onClose,
  timezone,
  onOpenContact,
}) => {
  const {
    decisions,
    total,
    loading,
    currentPage,
    pageSize,
    goToPage,
    resetPage,
    changePageSize,
  } = useFlywheelFollowUpsGQL({
    enabled: isOpen,
    decisionType: filter.decisionType,
    strategySegment: filter.strategySegment,
  });

  useEffect(() => {
    if (isOpen) resetPage();
  }, [isOpen, filter.decisionType, filter.strategySegment, resetPage]);

  return (
    <StatsModal
      isOpen={isOpen}
      onClose={onClose}
      title={filter.title}
      timezone={timezone}
      currentPage={currentPage}
      pageSize={pageSize}
      totalCount={total}
      isLoading={loading}
      onNextPage={() => goToPage(currentPage + 1)}
      onPrevPage={() => goToPage(currentPage - 1)}
      onPageChange={goToPage}
      onPageSizeChange={changePageSize}
      paginationLabel="follow-ups"
      onRowClick={(item) => {
        const contactId = item.contactId as string | null | undefined;
        if (contactId) onOpenContact(contactId);
      }}
      columns={[
        {
          key: 'contact',
          header: 'Name',
          width: '260px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            if (!decision.contactId) {
              return <span className="text-muted-foreground">Unknown contact</span>;
            }
            return (
              <button
                className="text-primaryfortext font-medium underline text-left"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenContact(decision.contactId as string);
                }}
              >
                {contactName(decision)}
              </button>
            );
          },
        },
        {
          key: 'decisionType',
          header: 'Type',
          width: '120px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            const decisionType = presentText(decision.decisionType);
            if (!decisionType) {
              return <span className="text-muted-foreground">-</span>;
            }
            const meta = decisionTypeBadge(decisionType);
            return (
              <span
                className={`inline-flex items-center gap-1 font-semibold px-2 py-px rounded-full text-[11px] leading-none whitespace-nowrap ${meta.bgClass} ${meta.colorClass}`}
              >
                {meta.icon}
                {getFollowUpTypeLabel(decisionType)}
              </span>
            );
          },
        },
        {
          key: 'strategyId',
          header: 'Strategy',
          width: '130px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            const strategy = decision.strategyId?.trim();
            // Match the rest of the stats columns — empty cells get a
            // plain "-" placeholder instead of an italic "Unknown"
            // label that read as a content value.
            return strategy ? (
              <span className="whitespace-nowrap">
                {strategy.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
        {
          key: 'scheduledFor',
          header: 'Scheduled',
          width: '190px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            return decision.scheduledFor ? (
              <span className="whitespace-nowrap">
                {formatDate(decision.scheduledFor, timezone, 'MMM D, YYYY h:mm A z')}
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
        {
          key: 'outcome',
          header: 'Outcome',
          width: '110px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            const outcome = presentText(decision.outcome);
            return outcome ? (
              <span className="whitespace-nowrap">
                {outcome.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : (
              <span className="text-muted-foreground">Pending</span>
            );
          },
        },
        {
          key: 'reasoning',
          header: 'Reasoning',
          width: '420px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            const text = presentText(decision.reasoning) || presentText(decision.decisionReason);
            if (!text) return <span className="text-muted-foreground">-</span>;
            return <ExpandableTextCell text={text} className="leading-relaxed" />;
          },
        },
      ]}
      data={
        decisions.map((decision) => ({
          ...decision,
          id: decision.decisionId,
        })) as unknown as Record<string, unknown>[]
      }
    />
  );
};

export const FlywheelContactsModal: FC<FlywheelContactsModalProps> = ({
  filter,
  isOpen,
  onClose,
  onOpenContact,
}) => {
  const {
    contacts,
    total,
    loading,
    currentPage,
    pageSize,
    goToPage,
    resetPage,
    changePageSize,
  } = useFlywheelContactsGQL(isOpen);

  useEffect(() => {
    if (isOpen) resetPage();
  }, [isOpen, resetPage]);

  return (
    <StatsModal
      isOpen={isOpen}
      onClose={onClose}
      title={filter.title}
      currentPage={currentPage}
      pageSize={pageSize}
      totalCount={total}
      isLoading={loading}
      onNextPage={() => goToPage(currentPage + 1)}
      onPrevPage={() => goToPage(currentPage - 1)}
      onPageChange={goToPage}
      onPageSizeChange={changePageSize}
      paginationLabel="contacts"
      onRowClick={(item) => onOpenContact(item.contactId as string)}
      columns={[
        {
          key: 'name',
          header: 'Name',
          width: '200px',
          cell: (item: Record<string, unknown>) => {
            const contact = item as unknown as GQLFlywheelContact;
            const name =
              [contact.firstName, contact.lastName].map(presentText).filter(Boolean).join(' ') ||
              presentText(contact.email) ||
              presentText(contact.phone) ||
              'Unknown contact';
            return (
              <button
                className="text-primaryfortext font-medium underline text-left"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenContact(contact.contactId);
                }}
              >
                {name}
              </button>
            );
          },
        },
        {
          key: 'phone',
          header: 'Phone',
          width: '160px',
          cell: (item: Record<string, unknown>) => (
            <TelLink value={item.phone as string | null | undefined} />
          ),
        },
        {
          key: 'email',
          header: 'Email',
          width: '220px',
          // Clamp to the column (like the contacts table) so long addresses truncate
          // instead of running into Recent Outcome; the full address shows on hover.
          cell: (item: Record<string, unknown>) => (
            <div className="max-w-0 min-w-full" title={presentText(item.email)}>
              <EmailLink value={item.email as string | null | undefined} className="block truncate" />
            </div>
          ),
        },
        {
          key: 'recentOutcome',
          header: 'Recent Outcome',
          width: '160px',
          cell: (item: Record<string, unknown>) => {
            const outcome = presentText(item.recentOutcome);
            return outcome ? (
              <span className="whitespace-nowrap">
                {outcome.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
        {
          key: 'flows',
          header: 'Flows',
          width: '300px',
          cell: (item: Record<string, unknown>) => {
            const names = flowNames(item as unknown as GQLFlywheelContact);
            if (names.length === 0) return <span className="text-muted-foreground">-</span>;
            return <ExpandableTagsCell tags={names} badgeClassName="badge-blue" />;
          },
        },
      ]}
      data={contacts.map((contact) => ({ ...contact, id: contact.contactId }))}
    />
  );
};
